import { CookieManager } from '@Utilities/CookieManager';
import "@Base/Engagement/Recommendations.scss";
import { Product } from "@Base/Engagement/Product";
import { ProductCarousel } from "@Base/Engagement/ProductCarousel";
import { ProductSize } from "@Base/Engagement/ProductSize";

import { Cart } from "@Utilities/Cart";
import { Tenant } from "@Utilities/Tenant";
import { TrackingService } from "@Api/TrackingService";

import { Media } from "@Utilities/Media";
import enquire from "enquire.js";
import { Fancyapps } from "@Libraries/Fancyapps";

import HcHoverRecs from "@Tenants/Hc/Templates/HoverRecs.hbs";
import HcProductCard from "@Tenants/Hc/Templates/ProductCard.hbs";
import FunProductCard from "@Tenants/Fun/Templates/ProductCard.hbs";
import RecentlyViewedSide from "@Templates/RecentlyViewedSide.hbs";

export class RecommendationBase
{
	public ReferrerCategoryID = 0;
	public ReferringCategoryAsyncAPI = false;
	public GetReferringCategoryCallback = null;

	private readonly cart: Cart;
	private readonly trackingService: TrackingService;

	constructor()
	{
		this.cart = new Cart();
		this.trackingService = new TrackingService();
	}

	public ConfigureFourUpCarousel = async (carouselSelector: string): Promise<void> =>
	{
		const mobileCount = 2;
		const desktopCount = 4;
		let displayCount = 0;
		const container = document.querySelector(carouselSelector) as HTMLElement;

		if (Media.IsMobile)
		{
			displayCount = mobileCount;
		}
		else
		{
			displayCount = desktopCount;
		}

		if (container !== null)
		{
			const carousel = container.querySelector(".fourup-carousel") as HTMLElement;
			const fancyapps = await Fancyapps.Import();

			const carouselInstance = new fancyapps.Carousel(carousel, {
				slidesPerPage: displayCount,
				center: false,
				Navigation: false
			});

			enquire.register(Media.MediaMobile, {
				match: () =>
				{
					carouselInstance.options.slidesPerPage = mobileCount;
					carouselInstance.updatePage();
				},
				unmatch: () =>
				{
					carouselInstance.options.slidesPerPage = desktopCount;
					carouselInstance.updatePage();
				}
			});
		}
	};

	///<summary>Configures the recently viewed container behavior.</summary>
	public ConfigureRightItems = (recommendations, recRecentSide: HTMLElement): void =>
	{
		const html = RecentlyViewedSide(recommendations);
		const rcnts: HTMLElement = document.getElementById("rcnts");

		if (rcnts !== null)
		{
			rcnts.innerHTML = html;

			recRecentSide.classList.remove("d-xxl-none");
			recRecentSide.classList.add("d-xxl-block");
		}
	};

	///<summary>Configures the carousel for the theme (with add to cart) on the middle of the product detail page.</summary>
	public ConfigureThemeAddItems = (el = ".rel-acc"): void =>
	{
		const container = document.querySelector(el) as HTMLElement;

		if (container !== null)
		{
			const sizeSelectors = container.querySelectorAll("select");
			const addRec = container.querySelectorAll(".add-rec");

			if (sizeSelectors.length > 0)
			{
				sizeSelectors.forEach((select) =>
				{
					select.addEventListener("change", (e) =>
					{
						const el = e.currentTarget as HTMLSelectElement;
						const price = el.options[el.selectedIndex].getAttribute("data-price");
						const item = el.closest(".ps-item");
						const priceEl = item.querySelector(".price");
						priceEl.textContent = Tenant.CurrencySymbol + price;
					});
				});
			}

			//Unbinding needs to be done so that there are not 2 addToCart click events on the buttons.
			//This happens when Initialize is call twice because both our own recommendations and 3rd party
			//recommendations are being used on the page.
			//The namespacing on the click event is necessary so that other click events are not removed.
			//In this case, a click event is used to record click-throughs to the 3rd party.
			if (addRec.length > 0)
			{
				addRec.forEach((addRec) =>
				{
					addRec.removeEventListener("click", this.AddRecHandler);
					addRec.addEventListener("click", this.AddRecHandler);
				});
			}
		}
	};

	public HydrateLocalPlacement = (rawData, utmParameter): object =>
	{
		let recommendations = [];
		let item = void 0;
		let sizeData = void 0;
		let sizes = void 0;
		let size = void 0;
		const currencyCookie = CookieManager.Cookies.Currency;

		const currencySymbol = CookieManager.GetCookie(currencyCookie.Key, currencyCookie.CurrencySymbol);
		const items = JSON.parse(rawData);

		for (let j = 0; j < items.length; j++)
		{
			sizeData = items[j].Sizes;
			sizes = [];

			for (let k = 0; k < sizeData.length; k++)
			{
				size = new ProductSize(sizeData[k].ProductSizeID, sizeData[k].Size, sizeData[k].Price, sizeData[k].RegularPrice, sizeData[k].DisplayOrder);
				sizes.push(size);
			}

			sizes = sizes.sort(
				function (a, b)
				{
					return a.DisplayOrder - b.DisplayOrder;
				});

			let url = items[j].Url;

			if (utmParameter)
			{
				url += `?utm_content=${utmParameter}`;
			}

			item = new Product(items[j].ProductID,
				sizes,
				items[j].LinkTitle,
				url,
				items[j].ThumbnailUrl,
				items[j].Price,
				items[j].IsOnClearance,
				items[j].IsOnSale,
				items[j].IsPreOrder,
				items[j].Rating,
				currencySymbol,
				items[j].IsExclusive,
				items[j].MadeByUs,
				null,
				utmParameter);

			recommendations.push(item);
		}

		return recommendations;
	};

	///<summary>Tranforms raw data from javascript call.</summary>
	public HydrateRecommendations = (rawData, utmParameter): object =>
	{
		let recommendationList = [];

		try
		{
			if (rawData)
			{
				for (let i = 0; i < rawData.length; i++)
				{
					this.HydratePlacement(rawData[i].strategy_name, rawData[i].results, recommendationList, utmParameter);
				}
			}

			return recommendationList;
		}
		catch (ex)
		{
			//continue
		}
	};

	public RenderFunProductCard = (divName, title, recommendations, utmParameter): void =>
	{
		let html = "";
		let carousel = void 0;

		const div = document.getElementById(divName) as HTMLElement;

		if (div !== null)
		{
			carousel = new ProductCarousel(divName, title, recommendations);
			carousel.AddToCart = true;
			html = FunProductCard(carousel);
			div.outerHTML = html;
			this.ConfigureThemeAddItems("#" + divName);
		}
	};

	public RenderHCProductCard = (divName, title, recommendations, utmParameter): void =>
	{
		let html = "";
		let carousel = void 0;

		const div = document.getElementById(divName) as HTMLElement;

		if (div !== null)
		{
			carousel = new ProductCarousel(divName, title, recommendations);
			carousel.AddToCart = true;
			html = HcProductCard(carousel);
			div.outerHTML = html;
			this.ConfigureThemeAddItems("#" + divName);
		}
	};

	public RenderHCHoverRecs = (divName, title, recommendations): void =>
	{
		let html = "";
		let carousel = void 0;

		const div = document.getElementById(divName) as HTMLElement;

		if (div !== null)
		{
			carousel = new ProductCarousel(divName, title, recommendations);
			html = HcHoverRecs(carousel);
			div.outerHTML = html;
		}
	};

	public RenderRecentlyViewedSide = (divName, recommendations): void =>
	{
		const div = document.getElementById(divName) as HTMLElement;
		this.ConfigureRightItems(recommendations, div);
	};

	private AddRecHandler = (e: Event): void =>
	{
		e.preventDefault();
		const target = e.currentTarget as HTMLElement;

		const form = target.closest(".ps-item, .item");
		const select = form.querySelector("select") as HTMLSelectElement;

		const productID = target.getAttribute("data-productid");
		const productSizeID = select.value;
		const cartItemOriginID = target.getAttribute("data-origin-id");
		const quantity = 1;
		const utmParameter = target.getAttribute("utm-parameter");

		const product = {
			ProductID: productID,
			ProductSizeID: productSizeID,
			Quantity: quantity,
			CartItemOriginID: cartItemOriginID
		};

		this.cart.AddProduct(product, null, utmParameter);
	};

	private HydratePlacement = (placementName, items, recommendationList, utmParameter): void =>
	{
		let recommendations = [];
		let item = void 0;
		let sizeData = void 0;
		let sizes = void 0;
		let size = void 0;
		const currencySymbol = Tenant.CurrencySymbol;

		recommendations = [];

		for (let j = 0; j < items.length; j++)
		{
			sizeData = items[j].facts.size_info;
			sizes = [];

			for (let k = 0; k < sizeData.length; k++)
			{
				if (sizeData[k].quantity > 0)
				{
					size = new ProductSize(sizeData[k].product_size_id, sizeData[k].size, sizeData[k].price, sizeData[k].regular_price, sizeData[k].display_order);
					sizes.push(size);
				}
			}

			sizes = sizes.sort(
				function (a, b)
				{
					return a.display_order - b.display_order;
				});

			let url = items[j].facts.relative_url;

			if (utmParameter)
			{
				url += `?utm_content=${utmParameter}`;
			}

			item = new Product(items[j].facts.product_id,
				sizes,
				items[j].facts.name,
				url,
				items[j].facts.image,
				items[j].facts.price,
				items[j].facts.is_on_clearance === "True",
				items[j].facts.is_on_sale === "True",
				items[j].facts.is_preorder === "True",
				items[j].facts.rating,
				currencySymbol,
				items[j].facts.is_exclusive === "True",
				items[j].facts.made_by_us === "True",
				null,
				utmParameter);

			recommendations.push(item);
		}

		if (recommendations.length > 0)
		{
			recommendationList[placementName] = recommendations;
		}

		return recommendationList;
	};
}